// src/encounter/services/use-speciality-practitioners.ts
// [PROD-3336] The practitioners who hold one speciality, doctors AND allied staff, for the referral dialog's optional
// practitioner picker. Contract (Practice.Aggregator #171, org from the token):
//   GET /api/v1/Practice/Specialities/{specialityId}/Practitioners
//   → [{ practitionerId (doctorSetupId or staffSetupId), name, kind: 'doctor' | 'staff', userId? }], active only, one per login.
// Patient stores the picked id as an opaque GUID, so either kind can be sent as `targetPractitionerId`.
// A failure is an ERROR here (not "no one"): the picker then falls back to the doctors list.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import type { ReferralPractitioner } from '../lib/referral-practitioners';

export const specialityPractitionersPath = (specialityId: string) =>
  `/api/v1/Practice/Specialities/${encodeURIComponent(specialityId)}/Practitioners`;

interface SpecialityPractitionerRow {
  practitionerId?: string | null;
  name?: string | null;
  kind?: 'doctor' | 'staff' | null;
  userId?: string | null;
}

/** `specialityId` null sends nothing (the PROD-1395 dietitian target has no speciality id). */
export function useSpecialityPractitioners(specialityId: string | null) {
  const organizationId = useAuthStore((s) => s.user?.organizationId ?? null);
  return useQuery<ReferralPractitioner[]>({
    queryKey: ['speciality-practitioners', organizationId, specialityId],
    enabled: !!specialityId,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }) => {
      const rows = await get<SpecialityPractitionerRow[]>(specialityPractitionersPath(specialityId as string), { signal });
      return (Array.isArray(rows) ? rows : [])
        .map((r) => ({ id: r.practitionerId?.trim() ?? '', name: r.name?.trim() || 'Practitioner' }))
        .filter((p) => p.id.length > 0)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}
